import type { EventReviewWarning } from '../types';

interface WarningMeta {
  label: string;
  icon: string;
  tooltip: string;
}

export const EVENT_REVIEW_WARNING_META = {
  'check-date': {
    label: 'Date unverified',
    icon: 'bi-exclamation-triangle',
    tooltip: "We could not confirm this date against the source. Open the source before making plans.",
  },
  'time-unverified': {
    label: 'Time unverified',
    icon: 'bi-clock',
    tooltip: 'The event time could not be verified against the source.',
  },
  'location-unverified': {
    label: 'Location unverified',
    icon: 'bi-geo-alt',
    tooltip: 'The event location could not be verified against the source.',
  },
  'source-incomplete': {
    label: 'Source incomplete',
    icon: 'bi-file-earmark-exclamation',
    tooltip: 'Only part of this source could be extracted. Other activities may be missing; this is not a date warning.',
  },
} satisfies Record<EventReviewWarning, WarningMeta>;

const WARNING_ORDER: readonly EventReviewWarning[] = [
  'check-date',
  'time-unverified',
  'location-unverified',
  'source-incomplete',
];

interface Props {
  warnings?: readonly EventReviewWarning[];
  /** Other warnings may be icon-only; date uncertainty always keeps a visible label. */
  compact?: boolean;
  className?: string;
}

export default function EventReviewWarningChips({
  warnings,
  compact = false,
  className = '',
}: Props) {
  if (!warnings?.length) return null;

  const visibleWarnings = WARNING_ORDER.filter((warning) => warnings.includes(warning));
  if (visibleWarnings.length === 0) return null;

  return (
    <span className={`d-inline-flex flex-wrap align-items-center gap-1${className ? ` ${className}` : ''}`}>
      <span className="visually-hidden">Event review warnings: </span>
      {visibleWarnings.map((warning) => {
        const meta = EVENT_REVIEW_WARNING_META[warning];
        return (
          <span
            key={warning}
            className={`badge rounded-pill text-bg-warning text-dark d-inline-flex align-items-center gap-1${compact ? ' p-1' : ''}`}
            title={meta.tooltip}
          >
            <i className={`bi ${meta.icon}`} aria-hidden="true" />
            <span className={compact && warning !== 'check-date' ? 'visually-hidden' : undefined}>{meta.label}</span>
          </span>
        );
      })}
    </span>
  );
}
